import type {ButtonHTMLAttributes} from 'react';

import {useState} from 'react';

import {cn} from '../../helpers/cn';

export type SwitchSize = 'sm' | 'md' | 'lg';

export type SwitchProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'children'> & {
  checked?: boolean;
  defaultChecked?: boolean;
  size?: SwitchSize;
  onChange?: (checked: boolean) => void;
};

const trackSizeClasses: Record<SwitchSize, string> = {
  sm: 'h-4 w-7',
  md: 'h-[22px] w-11',
  lg: 'h-7 w-14'
};

const thumbSizeClasses: Record<SwitchSize, string> = {
  sm: 'size-3',
  md: 'size-[18px]',
  lg: 'size-6'
};

/** Checked position via logical insetInlineStart (mirrors correctly in RTL). */
const thumbOnStartClasses: Record<SwitchSize, string> = {
  sm: 'start-[calc(100%-2px-12px)]',
  md: 'start-[calc(100%-2px-18px)]',
  lg: 'start-[calc(100%-2px-24px)]'
};

export function Switch({
  checked,
  defaultChecked = false,
  disabled,
  size = 'md',
  className,
  onChange,
  onClick,
  ...props
}: SwitchProps) {
  const isControlled = checked !== undefined;
  const [uncontrolledChecked, setUncontrolledChecked] = useState(defaultChecked);
  const isOn = isControlled ? checked : uncontrolledChecked;

  return (
    <button
      {...props}
      type="button"
      role="switch"
      aria-checked={isOn}
      disabled={disabled}
      className={cn(
        'ds-switch relative inline-flex shrink-0 cursor-pointer overflow-visible rounded-full border-0 p-0 transition-colors duration-200',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange',
        'disabled:cursor-not-allowed disabled:opacity-50',
        trackSizeClasses[size],
        isOn
          ? 'bg-orange [--ds-wave-color:var(--color-orange)]'
          : 'bg-gray-26 [--ds-wave-color:var(--color-gray-26)]',
        className
      )}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || disabled) return;
        const next = !isOn;
        if (!isControlled) {
          setUncontrolledChecked(next);
        }
        onChange?.(next);
      }}
    >
      <span
        aria-hidden
        className={cn(
          'ds-switch-handle absolute top-[2px] start-[2px] transition-[inset-inline-start] duration-200 ease-in-out',
          thumbSizeClasses[size],
          isOn ? thumbOnStartClasses[size] : 'start-[2px]'
        )}
      />
    </button>
  );
}
